import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AutocompleteInput({
  id,
  label,
  field,
  value,
  onChange,
  context = {},
  placeholder,
  testId,
  required = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const fetchSuggestions = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('warehouse_token');
        const params = { field, q };
        Object.entries(context).forEach(([k, v]) => {
          if (v && k !== field) params[k] = v;
        });
        const res = await axios.get(`${API}/picking/autocomplete`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestions(res.data.suggestions || []);
        setActiveIdx(-1);
      } catch (err) {
        setSuggestions([]);
      }
    }, 180);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (v.length >= 1) {
      setOpen(true);
      fetchSuggestions(v);
    } else {
      setOpen(false);
      setSuggestions([]);
    }
  };

  const handleFocus = () => {
    if (value && value.length >= 1) {
      setOpen(true);
      fetchSuggestions(value);
    } else {
      // Show top values (empty query) so warehouse staff can pick without typing
      setOpen(true);
      fetchSuggestions('');
    }
  };

  const pickSuggestion = (s) => {
    onChange(s);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Label htmlFor={id}>{label}{required && ' *'}</Label>
      <Input
        id={id}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="mono"
        data-testid={testId}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-[#09090B] rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 mono text-sm border-b border-[#F4F4F5] last:border-b-0 ${
                i === activeIdx ? 'bg-[#007AFF] text-white' : 'text-[#09090B] hover:bg-[#F4F4F5]'
              }`}
              data-testid={`${testId}-option-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
